import { Text, useBoolean, VStack } from '@chakra-ui/react';
import { isAfter } from 'date-fns';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Navigate, useLocation, useOutletContext } from 'react-router-dom';

import { AppError } from '../../../../framework/models/errors';
import { ListUtils } from '../../../../framework/utils/ListUtils';
import { TypeUtils } from '../../../../framework/utils/UtilityTypes';
import { pageKeywords, pagePaths } from '../../../constants/Page';
import { useGlobalStateMachine } from '../../../GlobalStore';
import { useEngeneNavigate } from '../../../hooks/navigate';
import { usePostOutsourceMemberReportWorkReport } from '../../../hooks/queries/outsourceMemberReport';
import useToastHook from '../../../hooks/ToastHook';
import {
  AssignWorkInfoDaySearchResult,
  AssignWorkInfoDaySearchResultResults,
} from '../../../models/gen/models';
import { Formatter } from '../../../utils/FormatUtils';
import { useConfirmModal } from '../../modals/confirm';
import { useSizeContext } from '../../SizeProvider';
import { OutsourceMemberReportOutletContext } from '../Root';
import WorkReportBody from './WorkReportBody';

/**
 * 就業報告内容プロパティ
 */
export type AssignWorkInfoProps = {
  /**
   * 表示モード
   */
  mode: 'edit' | 'view';
};

/**
 * 就業報告内容
 * @returns
 */
const AssignWorkInfo: React.FunctionComponent<AssignWorkInfoProps> = (
  props
) => {
  const { actions } = useGlobalStateMachine();
  const sizes = useSizeContext();
  const location = useLocation();
  const navigate = useEngeneNavigate();
  const {
    authCode,
    workReport: data,
    attendanceTypes,
    isLoading,
  } = useOutletContext<OutsourceMemberReportOutletContext>();
  const convertedData = React.useMemo(
    () => ({
      ...data,
      results: data?.results
        ?.sort((a, b) =>
          a.assignedWorkerWorkInfo.workTimeFrom !==
          b.assignedWorkerWorkInfo.workTimeFrom
            ? ListUtils.sort<AssignWorkInfoDaySearchResultResults>(
                (result) => result.assignedWorkerWorkInfo.workTimeFrom
              )(a, b)
            : a.matterSummary.requestTime !== b.matterSummary.requestTime
              ? ListUtils.sort<AssignWorkInfoDaySearchResultResults>(
                  (result) => result.matterSummary.requestTime
                )(a, b)
              : ListUtils.sort<AssignWorkInfoDaySearchResultResults>(
                  (result) => result.assignedWorkerSummary.assignWorkerId
                )(a, b)
        )
        .map((result) => {
          const date = data.reference?.dailyWork?.date;
          return {
            ...result,
            assignedWorkerWorkInfo: {
              ...result.assignedWorkerWorkInfo,
              workTimeFrom: Formatter.dateTimeString(
                TypeUtils.isNilString(data) ||
                  TypeUtils.isNilString(
                    result.assignedWorkerWorkInfo.workTimeFrom
                  )
                  ? `${date} 08:00:00`
                  : result.assignedWorkerWorkInfo.workTimeFrom
              ),
              workTimeTo: Formatter.dateTimeString(
                TypeUtils.isNilString(data) ||
                  TypeUtils.isNilString(
                    result.assignedWorkerWorkInfo.workTimeTo
                  )
                  ? `${date} 17:00:00`
                  : result.assignedWorkerWorkInfo.workTimeTo
              ),
              overtimeApprovedTime: Formatter.dateTimeString(
                TypeUtils.isNilString(data) ||
                  TypeUtils.isNilString(
                    result.assignedWorkerWorkInfo.overtimeApprovedTime
                  )
                  ? `${date} 12:00:00`
                  : result.assignedWorkerWorkInfo.overtimeApprovedTime
              ),
              restTime:
                TypeUtils.isNilString(
                  result.assignedWorkerWorkInfo.workTimeFrom
                ) ||
                TypeUtils.isNilString(result.assignedWorkerWorkInfo.workTimeTo)
                  ? 60
                  : result.assignedWorkerWorkInfo.restTime,
            },
          };
        }),
    }),
    [data]
  );
  const form = useForm<AssignWorkInfoDaySearchResult>();
  React.useEffect(() => {
    if (!isLoading && TypeUtils.isNonNil(data)) {
      form.reset(convertedData);
    }
  }, [isLoading, convertedData]);
  const ATTENDANCE_TYPE_PRESENT = attendanceTypes?.find(
    (attendanceType) => attendanceType.name === '出勤'
  )?.id;
  const { toast } = useToastHook();
  const [recalculateDailyWorkTrigger, { toggle: recalculateDailyWork }] =
    useBoolean(false);

  const matterSize = React.useMemo(
    () => (TypeUtils.isNil(data?.results) ? 0 : data!.results!.length),
    [data?.results]
  );
  const postWorkReport = usePostOutsourceMemberReportWorkReport();
  const { ConfirmModal, onOpen } =
    useConfirmModal<AssignWorkInfoDaySearchResult>();

  // const { DailyWork: DailyWorkArea, isAbsence, toggle } = useDailyWork();

  // 全現場の作業開始時刻のうち、一番古いものを取得
  const workTimeFrom = React.useMemo(() => {
    const effectiveAttendanceTypeIndexes = form
      .getValues(
        Array(matterSize)
          .fill(null)
          .map(
            (_, index) =>
              `results.${index}.assignedWorkerWorkInfo.attendanceTypeId` as any
          )
      )
      .map((id, index) => (id === ATTENDANCE_TYPE_PRESENT ? index : null))
      .filter((index) => index !== null);

    const values = form
      .getValues(
        Array(matterSize)
          .fill(null)
          .map(
            (_, index) =>
              `results.${index}.assignedWorkerWorkInfo.workTimeFrom` as any
          )
      )
      .filter(
        (value, index) =>
          effectiveAttendanceTypeIndexes.includes(index) &&
          !TypeUtils.isNil(value) &&
          value !== ''
      );

    return values.length > 0
      ? Formatter.dateTime(
          new Date(
            values.reduce((prev: string, cur: string) =>
              (Date.parse(Formatter.toSlash(prev)) || 0) >
              (Date.parse(Formatter.toSlash(cur)) || 0)
                ? cur
                : prev
            )
          )
        )
      : null;
  }, [form, recalculateDailyWorkTrigger]);

  // 全現場の作業終了時刻のうち、一番新しいものを取得
  const workTimeTo = React.useMemo(() => {
    const effectiveAttendanceTypeIndexes = form
      .getValues(
        Array(matterSize)
          .fill(null)
          .map(
            (_, index) =>
              `results.${index}.assignedWorkerWorkInfo.attendanceTypeId` as any
          )
      )
      .map((id, index) => (id === ATTENDANCE_TYPE_PRESENT ? index : null))
      .filter((index) => index !== null);

    const values = form
      .getValues(
        Array(matterSize)
          .fill(null)
          .map(
            (_, index) =>
              `results.${index}.assignedWorkerWorkInfo.workTimeTo` as any
          )
      )
      .filter(
        (value, index) =>
          effectiveAttendanceTypeIndexes.includes(index) &&
          !TypeUtils.isNil(value) &&
          value !== ''
      );

    return values.length > 0
      ? Formatter.dateTime(
          new Date(
            values.reduce((prev: string, cur: string) =>
              (Date.parse(Formatter.toSlash(prev)) || 0) >
              (Date.parse(Formatter.toSlash(cur)) || 0)
                ? prev
                : cur
            )
          )
        )
      : null;
  }, [form, recalculateDailyWorkTrigger]);

  // 総作業時間(全現場の作業時間の総和)を取得
  const totalWorkTime: number = React.useMemo(() => {
    const effectiveAttendanceTypeIndexes = form
      .getValues(
        Array(matterSize)
          .fill(null)
          .map(
            (_, index) =>
              `results.${index}.assignedWorkerWorkInfo.attendanceTypeId` as any
          )
      )
      .map((id, index) => (id === ATTENDANCE_TYPE_PRESENT ? index : null))
      .filter((index) => index !== null);

    return form
      .getValues(
        Array(matterSize)
          .fill(null)
          .map(
            (_, index) =>
              `results.${index}.assignedWorkerWorkInfo.workTime` as any
          )
      )
      .filter(
        (value, index) =>
          effectiveAttendanceTypeIndexes.includes(index) &&
          !TypeUtils.isNil(value)
      )
      .map((value) => parseInt(value.toString()))
      .filter((value) => !TypeUtils.isNilNumber(value))
      .reduce((prev: number, cur: number) => prev + cur, 0);
  }, [form, recalculateDailyWorkTrigger]);

  // 残業有無(総作業時間が8時間を越える)
  const isOvertime = React.useMemo(
    () =>
      totalWorkTime > 480 &&
      !TypeUtils.isNilString(workTimeFrom) &&
      !TypeUtils.isNilString(workTimeTo),
    [totalWorkTime, recalculateDailyWorkTrigger]
  );

  // 保存前バリデーション
  const validateForSave = () => {
    const warns: string[] = [];
    const regexp = new RegExp(/^\d*[05]$/);
    Array(matterSize)
      .fill(null)
      .forEach((_, index) => {
        // 作業状況
        const attendanceTypeId = form.getValues(
          `results.${index}.assignedWorkerWorkInfo.attendanceTypeId`
        );
        // 作業開始
        const workTimeFrom = form.getValues(
          `results.${index}.assignedWorkerWorkInfo.workTimeFrom`
        );
        // 作業終了
        const workTimeTo = form.getValues(
          `results.${index}.assignedWorkerWorkInfo.workTimeTo`
        );
        // 休憩時間
        const restTime = form.getValues(
          `results.${index}.assignedWorkerWorkInfo.restTime`
        );

        // 作業状況が出勤の場合、開始時刻 < 終了時刻 であること
        if (
          attendanceTypeId === ATTENDANCE_TYPE_PRESENT &&
          isAfter(
            Formatter.createDate(workTimeFrom!),
            Formatter.createDate(workTimeTo!)
          )
        ) {
          warns.push(`現場${index + 1}：開始時刻 < 終了時刻 で入力して下さい`);
        }

        // 作業状況が出勤の場合、休憩時間は5分単位であること
        if (
          attendanceTypeId === ATTENDANCE_TYPE_PRESENT &&
          !regexp.test('' + restTime)
        ) {
          warns.push(`現場${index + 1}：休憩時刻は5分単位 で入力して下さい`);
        }
      });
    return warns;
  };

  // 保存ボタンの制御
  const [canSave, setCanSave] = useState(false);
  // 保存ボタンの制御に関わる項目で再計算必要となるタイミングで呼び出すメソッド
  const checkCanSave = () => {
    const b = Array(matterSize)
      .fill(null)
      .some((_, index) => {
        // 作業状況
        const attendanceTypeId = form.getValues(
          `results.${index}.assignedWorkerWorkInfo.attendanceTypeId`
        );
        // 休憩時間
        const restTime = form.getValues(
          `results.${index}.assignedWorkerWorkInfo.restTime`
        );
        // 残業承認時刻
        const overtimeApprovedTime = form.getValues(
          `results.${index}.assignedWorkerWorkInfo.overtimeApprovedTime`
        );
        // 残業承認者
        const overtimeApprovedPerson = form.getValues(
          `results.${index}.assignedWorkerWorkInfo.overtimeApprovedPerson`
        );
        // 作業時間
        const workTime =
          form.getValues(`results.${index}.assignedWorkerWorkInfo.workTime`) ||
          0;

        // チェック：入力必須
        // 以下 OR 条件で何かに引っかかったら true -> save できない
        const checkRequiredsNotFullfilled: boolean =
          TypeUtils.isNilNumber(attendanceTypeId) ||
          (attendanceTypeId === ATTENDANCE_TYPE_PRESENT &&
            form
              .getValues([
                `results.${index}.assignedWorkerWorkInfo.workTimeFrom`,
                `results.${index}.assignedWorkerWorkInfo.workTimeTo`,
                `results.${index}.assignedWorkerWorkInfo.restTime`,
              ])
              .filter(TypeUtils.isNilStrict).length > 0) ||
          (TypeUtils.isNilStrict(restTime) ? false : restTime < 0) ||
          (workTime > 480 &&
            (TypeUtils.isNilString(overtimeApprovedTime) ||
              TypeUtils.isNilString(overtimeApprovedPerson)));

        return checkRequiredsNotFullfilled;
      });
    setCanSave(!b);
  };
  React.useEffect(() => {
    // 画面表示時に一度再計算
    checkCanSave();
  });

  // edit で来ても、対象が外注で全ての手配職人が承認済みなら(承認時刻が入っていれば)、編集不可として VIEW モードへ飛ばす。
  // 編集可否は勤怠申請の有無のみで判定されるが、外注の場合は全ての手配職人が了承済の時点で編集不可となるため(手当・ポイントの編集が無い)ため
  if (
    props.mode === 'edit' &&
    data?.results?.every(
      (result) =>
        !TypeUtils.isNilString(
          result.assignedWorkerSummary.reportApprovedTime
        ) &&
        !TypeUtils.isNilString(result.assignedWorkerSummary.outsourcerUserCode)
    )
  ) {
    return (
      <Navigate
        replace
        to={`${pagePaths.WORK_CONTENT}/${pageKeywords.mode.VIEW}${location.search}`}
      />
    );
  }

  return (
    <VStack
      alignItems="start"
      as="form"
      fontSize={sizes.fontSize.rawMessage}
      // onSubmit={form.handleSubmit(onOpen)}
      width={sizes.width.styledMessageList}
    >
      <VStack alignItems="start" width="full">
        <Text margin="0px !important">
          {Formatter.dateOfWeekString(data?.reference?.dailyWork?.date)}
        </Text>
        <Text margin="0px !important">
          {data?.reference?.userSummary?.name}
        </Text>
      </VStack>
      {matterSize === 0 ? null : (
        <FormProvider {...form}>
          {/* <DailyWorkArea
            isOvertime={isOvertime}
            matterSize={matterSize}
            mode={props.mode}
            totalWorkTime={totalWorkTime}
            workTimeFrom={workTimeFrom}
            workTimeTo={workTimeTo}
          />
          <Matters
            isAbsence={false}
            matterIndex={0}
            matterSize={matterSize}
            mode={props.mode}
            toggle={() => null}
            onChangedRelatedToSavable={checkCanSave}
            onChangedRelatedToDailyWork={recalculateDailyWork}
          /> */}
          {/* <Box width="full">
            {props.mode === 'edit' ? (
              <SaveButton
                isDisabled={!canSave}
                type="button"
                onClick={() => {
                  const warns = validateForSave();
                  if (warns.length > 0) {
                    warns.forEach((warn, index) => {
                      setTimeout(() => {
                        toast(warn, 'warning');
                      }, index * 100);
                    });
                  } else {
                    form.handleSubmit(onOpen)();
                  }
                }}
              />
            ) : data?.results?.every(
                (result) =>
                  !TypeUtils.isNilString(
                    result.assignedWorkerSummary.reportApprovedTime
                  )
              ) ? null : (
              <EditButton
                onClick={() =>
                  navigate(
                    `${pagePaths.WORK_CONTENT}/${params.date}/${params.usercode}/${pageKeywords.mode.EDIT}${location.search}`
                  )
                }
              />
            )}
          </Box> */}
          {/* <Box width="full">
            {props.mode === 'edit' ? (
              <SaveButton
                isDisabled={!canSave}
                type="button"
                onClick={() => {
                  const warns = validateForSave();
                  if (warns.length > 0) {
                    warns.forEach((warn, index) => {
                      setTimeout(() => {
                        toast(warn, 'warning');
                      }, index * 100);
                    });
                  } else {
                    form.handleSubmit(onOpen)();
                  }
                }}
              />
            ) : null}
          </Box> */}
          <WorkReportBody
            canSave={canSave}
            checkCanSave={checkCanSave}
            isOvertime={isOvertime}
            matterSize={matterSize}
            mode={props.mode}
            onOpen={onOpen}
            recalculateDailyWork={recalculateDailyWork}
            totalWorkTime={totalWorkTime}
            validateForSave={validateForSave}
            workTimeFrom={workTimeFrom}
            workTimeTo={workTimeTo}
            ATTENDANCE_TYPE_PRESENT={ATTENDANCE_TYPE_PRESENT}
          />
        </FormProvider>
      )}
      {/* <BackButton onClick={() => navigate(-1)} /> */}
      <ConfirmModal
        message="保存してよろしいですか?"
        onOk={(value) => {
          actions.setGuard(true);
          postWorkReport.mutate(
            {
              authCode: authCode,
              saveParam: {
                assignWorkers: value?.results
                  // ?.filter((result) =>
                  //   TypeUtils.isNilString(
                  //     result.assignedWorkerSummary.reportApprovedTime
                  //   )
                  // )
                  ?.sort(
                    ListUtils.sort((result) =>
                      TypeUtils.isNilString(
                        result.assignedWorkerWorkInfo.workTimeFrom
                      )
                        ? undefined
                        : Date.parse(result.assignedWorkerWorkInfo.workTimeFrom)
                    )
                  )
                  .map((result, index) => {
                    const isPresent =
                      result.assignedWorkerWorkInfo.attendanceTypeId ===
                      ATTENDANCE_TYPE_PRESENT;
                    const isOvertime =
                      TypeUtils.isNilStrict(
                        result.assignedWorkerWorkInfo.workTimeFrom
                      ) ||
                      TypeUtils.isNilStrict(
                        result.assignedWorkerWorkInfo.workTimeTo
                      )
                        ? false
                        : (Date.parse(
                            result.assignedWorkerWorkInfo.workTimeTo || ''
                          ) -
                            Date.parse(
                              result.assignedWorkerWorkInfo.workTimeFrom || ''
                            )) /
                            (1000 * 60) -
                            parseInt(
                              result.assignedWorkerWorkInfo.restTime?.toString() ||
                                '0'
                            ) >
                          480;

                    return {
                      assignWorkerId:
                        result.assignedWorkerSummary.assignWorkerId,
                      assignWorkerVersion:
                        result.assignedWorkerSummary.assignWorkerVersion,
                      userCodeOutsourceMember: TypeUtils.toStrictValue(
                        result.assignedWorkerSummary.outsourcerUserCode
                      ),
                      taskIds: result.assignedWorkerSummary.taskIds,
                      attendanceTypeId:
                        result.assignedWorkerWorkInfo.attendanceTypeId,

                      // 出勤時以外クリア
                      beLate: isPresent
                        ? !!result.assignedWorkerWorkInfo.beLate
                        : false,

                      // 出勤時以外クリア
                      leaveWork: isPresent
                        ? !!result.assignedWorkerWorkInfo.leaveWork
                        : false,

                      // 出勤時以外クリア
                      timeFrom: isPresent
                        ? Formatter.dateTimeString(
                            result.assignedWorkerWorkInfo.workTimeFrom,
                            true
                          )
                        : undefined,

                      // 出勤時以外クリア
                      timeTo: isPresent
                        ? Formatter.dateTimeString(
                            result.assignedWorkerWorkInfo.workTimeTo,
                            true
                          )
                        : undefined,

                      // 出勤時以外クリア
                      restTime: isPresent
                        ? TypeUtils.toStrictValue(
                            parseInt(
                              result.assignedWorkerWorkInfo.restTime?.toString() ||
                                ''
                            )
                          )
                        : undefined,

                      // 出勤時かつ残業発生時以外クリア
                      overtimeApprovedTime:
                        isPresent && isOvertime
                          ? Formatter.dateTimeString(
                              result.assignedWorkerWorkInfo
                                .overtimeApprovedTime,
                              true
                            )
                          : undefined,

                      // 出勤時かつ残業発生時以外クリア
                      overtimeApprovedPerson:
                        isPresent && isOvertime
                          ? TypeUtils.toStrictValue(
                              result.assignedWorkerWorkInfo
                                .overtimeApprovedPerson
                            )
                          : undefined,

                      reportMemo: TypeUtils.toStrictValue(
                        result.assignedWorkerWorkInfo.reportMemo
                      ),
                    };
                  }),
              },
            },
            {
              onSuccess: () => {
                navigate(
                  // `${pagePaths.WORK_CONTENT}/${pageKeywords.mode.VIEW}${location.search}`
                  pagePaths.SUCCEEDED
                );
                actions.setGuard(false);
              },
              onError: (error) => {
                toast(
                  error instanceof AppError
                    ? error
                    : '正常に処理を完了できませんでした．代表者に報告して下さい'
                );
                actions.setGuard(false);
              },
            }
          );
        }}
      />
    </VStack>
  );
};

export default AssignWorkInfo;
